/**
 * /forgot — request a password reset link via email.
 *
 * Backend always responds 204 (we never leak whether the email exists), so
 * the UI just shows a generic confirmation regardless of input. If the email
 * is real, the user gets an email with a link to /reset/{token}.
 */

import { useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";

import { LogoMark, LogoWordmark } from "@m2s/design-system";

import { api } from "@/lib/api";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
    } catch {
      // backend returns 204 in the happy path; any other status we still
      // show the same UI so we don't leak account existence.
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  };

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <LogoMark size={48} />
          </div>
          <LogoWordmark size={20} />
          <h1 style={heading}>Reset your password</h1>
        </div>

        {sent ? (
          <>
            <p style={blurb}>
              If <strong>{email}</strong> is registered, we just sent a password reset link.
              Check your inbox — the link expires in 1 hour.
            </p>
            <p style={{ ...blurb, marginTop: 16, color: "var(--mute)", fontSize: 12.5 }}>
              Didn't get it? Check spam, or try again in a minute.
            </p>
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <Link to="/login" style={backLink}>
                ‹ Back to sign in
              </Link>
            </div>
          </>
        ) : (
          <form onSubmit={onSubmit}>
            <p style={blurb}>
              Enter your email and we'll send you a link to set a new password.
            </p>
            <label style={{ display: "block", marginTop: 16 }}>
              <span style={label}>Email</span>
              <input
                type="email"
                required
                autoFocus
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={input}
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              style={{
                ...primaryBtn,
                marginTop: 16,
                opacity: submitting ? 0.6 : 1,
                cursor: submitting ? "wait" : "pointer",
              }}
            >
              {submitting ? "Sending…" : "Send reset link"}
            </button>
            <div style={{ textAlign: "center", marginTop: 12 }}>
              <Link to="/login" style={backLink}>
                ‹ Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------- styles ----------

const wrap: CSSProperties = {
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  padding: 24,
  background: "var(--canvas)",
};

const card: CSSProperties = {
  width: "100%",
  maxWidth: 400,
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 32,
};

const heading: CSSProperties = {
  fontSize: 22,
  fontWeight: 500,
  letterSpacing: "-0.2px",
  margin: "16px 0 0",
  color: "var(--body)",
};

const blurb: CSSProperties = {
  fontSize: 13.5,
  lineHeight: 1.55,
  color: "var(--body)",
  margin: 0,
};

const label: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 6,
};

const input: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "9px 12px",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
};

const primaryBtn: CSSProperties = {
  width: "100%",
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "10px 14px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const backLink: CSSProperties = {
  fontSize: 12.5,
  color: "var(--mute)",
  textDecoration: "none",
};
